import { useCallback, useRef } from "react";

/**
 * Aggregates multiple viewport-intersection pause sources into a single
 * `setAutoScrollPaused` call. Multiple in-progress widgets may be visible at
 * once (e.g. thinking row + review checklist); auto-scroll stays paused until
 * every source reports inactive.
 */
export function useAutoScrollPauseRegistry(
  setAutoScrollPaused: (paused: boolean) => void,
) {
  const sourcesRef = useRef(new Set<string>());

  const syncPaused = useCallback(() => {
    setAutoScrollPaused(sourcesRef.current.size > 0);
  }, [setAutoScrollPaused]);

  const reportPause = useCallback(
    (id: string, shouldPause: boolean) => {
      if (shouldPause) {
        sourcesRef.current.add(id);
      } else {
        sourcesRef.current.delete(id);
      }
      syncPaused();
    },
    [syncPaused],
  );

  const resetPauseSources = useCallback(() => {
    sourcesRef.current.clear();
    setAutoScrollPaused(false);
  }, [setAutoScrollPaused]);

  return { reportPause, resetPauseSources };
}
